import { Alert, Box, Divider, Link, Snackbar, Tab, Tabs, Typography } from "@mui/material";
import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ClaudeTutorial from "../../../../assets/McpServer/videos/claude-tutorial.mp4";
import { CopyIcon } from "../../../../assets/images/icons/CopyIcon.tsx";
import { DeviceDesktopIcon } from "../../../../assets/images/icons/DeviceDesktopIcon.tsx";
import ExternalLink from "../../../../assets/images/icons/ExternalLink.tsx";
import { LongArrowRightIcon } from "../../../../assets/images/icons/LongArrowRightIcon.tsx";
import { ShieldCheckIcon } from "../../../../assets/images/icons/ShieldCheckIcon.tsx";
import { Environment, getCurrentEnv } from "../../../../config/env.ts";
import LinkConstants from "../../../../constants/LinkConstants.ts";
import { useLogEvents } from "../../../../hooks/useLogEvents";
import VideoPopup from "../../../Popups/VideoPopup/VideoPopup.tsx";
import BriaButton from "../../../common/BriaButton/BriaButton.tsx";
import CopyApiTokenButton from "../../../common/OverviewFooter/CopyApiTokenButton.tsx";
import styles from "./McpServerInstallSection.module.scss";

export enum MCPTabs {
	QUICKTESTING = "quickTesting",
	SERVERTOSERVER = "serverToServer",
}

const McpServerInstallSection = () => {
	const { t } = useTranslation("translation", { keyPrefix: "mcpServer.installSection" });
	const {
		logImageGenerationMcpCopyUrlClick,
		logImageGenerationMcpServerToServerTabClick,
		logImageGenerationMcpQuickTestingTabClick,
		logImageGenerationMcpCreateApiTokenClick,
		logImageGenerationMcpCopyApiTokenClick,
		logImageGenerationMcpVideoExampleClick,
		logImageGenerationMcpApiTokenDocsClick,
		logImageGenerationMcpBearerAuthDocsClick,
		logImageEditingMcpCopyUrlClick,
		logImageEditingMcpServerToServerTabClick,
		logImageEditingMcpQuickTestingTabClick,
		logImageEditingMcpCreateApiTokenClick,
		logImageEditingMcpCopyApiTokenClick,
		logImageEditingMcpVideoExampleClick,
		logImageEditingMcpApiTokenDocsClick,
		logImageEditingMcpBearerAuthDocsClick,
	} = useLogEvents();
	const [openVideoExamplePopup, setOpenVideoExamplePopup] = useState(false);
	const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
	const [activeTab, setActiveTab] = useState(MCPTabs.QUICKTESTING);
	const isImageEditingTab = window.location.pathname.includes("image-editing");

	// Function to determine which event to trigger based on context
	const getAfterCopyAction = () => {
		if (isImageEditingTab) {
			return logImageEditingMcpCopyApiTokenClick;
		} else {
			return logImageGenerationMcpCopyApiTokenClick;
		}
	};

	const getAfterCreateAction = () => {
		if (isImageEditingTab) {
			return logImageEditingMcpCreateApiTokenClick;
		} else {
			return logImageGenerationMcpCreateApiTokenClick;
		}
	};

	const handleVideoExampleClick = () => {
		setOpenVideoExamplePopup(true);
		if (isImageEditingTab) {
			logImageEditingMcpVideoExampleClick();
		} else {
			logImageGenerationMcpVideoExampleClick();
		}
	};

	const handleApiTokenDocsClick = () => {
		window.open(LinkConstants.BRIA_MCP_AUTHENTICATION, "_blank");
		if (isImageEditingTab) {
			logImageEditingMcpApiTokenDocsClick();
		} else {
			logImageGenerationMcpApiTokenDocsClick();
		}
	};

	const handleBearerAuthDocsClick = () => {
		window.open(LinkConstants.BRIA_MCP_AUTHENTICATION, "_blank");
		if (isImageEditingTab) {
			logImageEditingMcpBearerAuthDocsClick();
		} else {
			logImageGenerationMcpBearerAuthDocsClick();
		}
	};

	const getMcpServerLink = () => {
		switch (getCurrentEnv()) {
			case Environment.production:
				return LinkConstants.BRIA_MCP_SERVER_PROD;
			case Environment.integration:
				return LinkConstants.BRIA_MCP_SERVER_INT;
			default:
				return LinkConstants.BRIA_MCP_SERVER_PROD;
		}
	};

	const handleCopyUrl = async () => {
		await navigator.clipboard.writeText(getMcpServerLink());
		setIsSnackbarOpen(true);

		// Trigger appropriate event based on current context
		if (isImageEditingTab) {
			logImageEditingMcpCopyUrlClick();
		} else {
			logImageGenerationMcpCopyUrlClick();
		}
	};

	const handleTabChange = (_event: React.SyntheticEvent, newValue: MCPTabs) => {
		setActiveTab(newValue);

		// Trigger appropriate event based on current context and tab
		if (newValue === MCPTabs.SERVERTOSERVER) {
			if (isImageEditingTab) {
				logImageEditingMcpServerToServerTabClick();
			} else {
				logImageGenerationMcpServerToServerTabClick();
			}
		} else if (newValue === MCPTabs.QUICKTESTING) {
			if (isImageEditingTab) {
				logImageEditingMcpQuickTestingTabClick();
			} else {
				logImageGenerationMcpQuickTestingTabClick();
			}
		}
	};

	return (
		<Box className={styles.mcpTabsContainer}>
			<Typography className={styles.title}>{t("title")}</Typography>
			<Tabs value={activeTab} onChange={handleTabChange} className={styles.tabs}>
				<Tab
					label={t("quickTesting.tabTitle")}
					value={MCPTabs.QUICKTESTING}
					className={clsx(styles.tab, activeTab === MCPTabs.QUICKTESTING && styles.activeTab)}
				/>
				<Tab
					label={t("serverToServer.tabTitle")}
					value={MCPTabs.SERVERTOSERVER}
					className={clsx(styles.tab, activeTab === MCPTabs.SERVERTOSERVER && styles.activeTab)}
				/>
			</Tabs>
			{activeTab === MCPTabs.QUICKTESTING && (
				<>
					<Box className={styles.cardsSection}>
						<Box className={styles.card}>
							<Box className={styles.cardTextSection}>
								<Typography className={styles.cardTitle}>
									{t("quickTesting.configureCard.title")}
								</Typography>
								<Typography className={styles.cardDescription}>
									{t("quickTesting.configureCard.description")}
								</Typography>
								<Typography
									component="a"
									className={styles.exampleLink}
									onClick={handleVideoExampleClick}
								>
									{t("quickTesting.configureCard.exampleLink")}
								</Typography>
							</Box>
							<BriaButton
								buttonType="primaryMedium"
								onClick={handleCopyUrl}
								className={styles.actionButton}
							>
								<CopyIcon strokeColor="#fff" />
								{t("quickTesting.configureCard.copyUrlButton")}
							</BriaButton>
						</Box>
						<Divider className={styles.divider} />

						<Box className={styles.card}>
							<Box className={styles.cardTextSection}>
								<Typography className={styles.cardTitle}>
									{t("quickTesting.startBriaCard.title")}
								</Typography>
								<Typography className={styles.cardDescription}>
									{t("quickTesting.startBriaCard.description")}
								</Typography>
							</Box>
						</Box>
					</Box>
				</>
			)}

			{activeTab === MCPTabs.SERVERTOSERVER && (
				<Box>
					<Box className={styles.card}>
						<Box className={styles.cardTextSection}>
							<Typography className={styles.cardTitle}>{t("serverToServer.title")}</Typography>
							<Box className={styles.authFlow}>
								<Box className={styles.step}>
									<DeviceDesktopIcon />
									<Typography>{t("serverToServer.yourApp")}</Typography>
								</Box>
								<Box className={styles.line}>
									<LongArrowRightIcon />
									<Typography>{t("serverToServer.requestWithToken")}</Typography>
								</Box>
								<Box className={styles.step}>
									<ShieldCheckIcon />
									<Typography>{t("serverToServer.briaMCP")}</Typography>
								</Box>
							</Box>
							<Typography className={styles.cardSubTitle}>{t("serverToServer.subTitle")}</Typography>
						</Box>
						<BriaButton buttonType="primaryMedium" onClick={handleCopyUrl} className={clsx(styles.actionButton, styles.STSCopyUrlBtn)}>
							<CopyIcon strokeColor="#fff" />
							{t("quickTesting.configureCard.copyUrlButton")}
						</BriaButton>
					</Box>
					<Divider className={styles.divider} />
					<Box className={styles.card}>
						<Box className={styles.cardTextSection}>
							<Typography className={styles.cardTitle}>
								{t("serverToServer.apiTokenAuth.title")}
							</Typography>
							<Typography className={styles.cardDescription}>
								{t("serverToServer.apiTokenAuth.description")}
							</Typography>
							<Link onClick={handleApiTokenDocsClick} className={styles.documentationLink}>
								<ExternalLink width="30" height="30" strokeWidth="1" />
								{t("serverToServer.apiTokenAuth.documentationLabel")}
							</Link>
						</Box>
						<CopyApiTokenButton
							buttonType="primaryMedium"
							withIcon={true}
							keyType="mcp"
							afterCopyAction={getAfterCopyAction()}
							afterCreateAction={getAfterCreateAction()}
						/>
					</Box>
					<Divider className={styles.divider} />
					<Box className={styles.card}>
						<Box className={styles.cardTextSection}>
							<Typography className={styles.cardTitle}>{t("serverToServer.bearerAuth.title")}</Typography>
							<Typography className={styles.cardDescription}>
								{t("serverToServer.bearerAuth.description")}
							</Typography>
							<Link onClick={handleBearerAuthDocsClick} className={styles.documentationLink}>
								<ExternalLink width="30" height="30" strokeWidth="1" />
								{t("serverToServer.bearerAuth.documentationLabel")}
							</Link>
						</Box>
					</Box>
				</Box>
			)}

			<VideoPopup
				isOpen={openVideoExamplePopup}
				onClose={() => setOpenVideoExamplePopup(false)}
				videoSrc={ClaudeTutorial}
			/>
			<Snackbar open={isSnackbarOpen} autoHideDuration={2000} onClose={() => setIsSnackbarOpen(false)}>
				<Alert onClose={() => setIsSnackbarOpen(false)} severity="success">
					{t("quickTesting.configureCard.copyToClipboardSuccess")}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default McpServerInstallSection;
